/**
 * Derives Tailwind-style names (`blue-500`, `gray-200`) for a palette so exports
 * read as semantic tokens instead of `color-1`, `color-2`.
 *
 * Rather than approximate hue/lightness boundaries (which mislabels near-grays
 * as colors and drifts families), we name each color by its nearest neighbor in
 * perceptual OKLab space against Tailwind's actual default palette. The name is
 * therefore always backed by a real, perceptually-closest reference swatch.
 * Names must be unique because they become CSS variable names and object keys,
 * so a collision falls through to the next-closest unused reference. Pure and
 * deterministic — same input, same output.
 */
import { converter } from 'culori';

const toOklab = converter('oklab');

/** Tailwind-style lightness steps, light → dark, aligned to each family row. */
const STEPS = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950] as const;

/**
 * Tailwind v3 default palette: each family's 11 steps (light → dark). Neutrals
 * are collapsed to a single `gray` family for consistent labeling — temperature
 * variants (slate/zinc/stone) would name visually similar grays inconsistently.
 */
const TAILWIND_PALETTE: Readonly<Record<string, readonly string[]>> = {
  gray: ['#f9fafb', '#f3f4f6', '#e5e7eb', '#d1d5db', '#9ca3af', '#6b7280', '#4b5563', '#374151', '#1f2937', '#111827', '#030712'],
  red: ['#fef2f2', '#fee2e2', '#fecaca', '#fca5a5', '#f87171', '#ef4444', '#dc2626', '#b91c1c', '#991b1b', '#7f1d1d', '#450a0a'],
  orange: ['#fff7ed', '#ffedd5', '#fed7aa', '#fdba74', '#fb923c', '#f97316', '#ea580c', '#c2410c', '#9a3412', '#7c2d12', '#431407'],
  amber: ['#fffbeb', '#fef3c7', '#fde68a', '#fcd34d', '#fbbf24', '#f59e0b', '#d97706', '#b45309', '#92400e', '#78350f', '#451a03'],
  yellow: ['#fefce8', '#fef9c3', '#fef08a', '#fde047', '#facc15', '#eab308', '#ca8a04', '#a16207', '#854d0e', '#713f12', '#422006'],
  lime: ['#f7fee7', '#ecfccb', '#d9f99d', '#bef264', '#a3e635', '#84cc16', '#65a30d', '#4d7c0f', '#3f6212', '#365314', '#1a2e05'],
  green: ['#f0fdf4', '#dcfce7', '#bbf7d0', '#86efac', '#4ade80', '#22c55e', '#16a34a', '#15803d', '#166534', '#14532d', '#052e16'],
  emerald: ['#ecfdf5', '#d1fae5', '#a7f3d0', '#6ee7b7', '#34d399', '#10b981', '#059669', '#047857', '#065f46', '#064e3b', '#022c22'],
  teal: ['#f0fdfa', '#ccfbf1', '#99f6e4', '#5eead4', '#2dd4bf', '#14b8a6', '#0d9488', '#0f766e', '#115e59', '#134e4a', '#042f2e'],
  cyan: ['#ecfeff', '#cffafe', '#a5f3fc', '#67e8f9', '#22d3ee', '#06b6d4', '#0891b2', '#0e7490', '#155e75', '#164e63', '#083344'],
  sky: ['#f0f9ff', '#e0f2fe', '#bae6fd', '#7dd3fc', '#38bdf8', '#0ea5e9', '#0284c7', '#0369a1', '#075985', '#0c4a6e', '#082f49'],
  blue: ['#eff6ff', '#dbeafe', '#bfdbfe', '#93c5fd', '#60a5fa', '#3b82f6', '#2563eb', '#1d4ed8', '#1e40af', '#1e3a8a', '#172554'],
  indigo: ['#eef2ff', '#e0e7ff', '#c7d2fe', '#a5b4fc', '#818cf8', '#6366f1', '#4f46e5', '#4338ca', '#3730a3', '#312e81', '#1e1b4b'],
  violet: ['#f5f3ff', '#ede9fe', '#ddd6fe', '#c4b5fd', '#a78bfa', '#8b5cf6', '#7c3aed', '#6d28d9', '#5b21b6', '#4c1d95', '#2e1065'],
  purple: ['#faf5ff', '#f3e8ff', '#e9d5ff', '#d8b4fe', '#c084fc', '#a855f7', '#9333ea', '#7e22ce', '#6b21a8', '#581c87', '#3b0764'],
  fuchsia: ['#fdf4ff', '#fae8ff', '#f5d0fe', '#f0abfc', '#e879f9', '#d946ef', '#c026d3', '#a21caf', '#86198f', '#701a75', '#4a044e'],
  pink: ['#fdf2f8', '#fce7f3', '#fbcfe8', '#f9a8d4', '#f472b6', '#ec4899', '#db2777', '#be185d', '#9d174d', '#831843', '#500724'],
  rose: ['#fff1f2', '#ffe4e6', '#fecdd3', '#fda4af', '#fb7185', '#f43f5e', '#e11d48', '#be123c', '#9f1239', '#881337', '#4c0519'],
};

interface Reference {
  name: string;
  l: number;
  a: number;
  b: number;
}

/** Flattened palette with OKLab coordinates precomputed once at module load. */
const REFERENCES: readonly Reference[] = Object.entries(TAILWIND_PALETTE).flatMap(
  ([family, hexes]) =>
    hexes.map((hex, i) => {
      const c = toOklab(hex)!;
      return { name: `${family}-${STEPS[i]}`, l: c.l, a: c.a, b: c.b };
    }),
);

/** Squared OKLab distance — monotonic with deltaE, cheaper, only used to rank. */
function squaredDistance(c: { l: number; a: number; b: number }, ref: Reference): number {
  return (c.l - ref.l) ** 2 + (c.a - ref.a) ** 2 + (c.b - ref.b) ** 2;
}

/** A unique `gray-N` name for the rare case that every nearby reference is taken. */
function numberedFallback(used: Set<string>): string {
  let n = 1;
  while (used.has(`gray-${n}`)) n++;
  return `gray-${n}`;
}

/** Family portion of a `family-step` name (family names contain no hyphens). */
const familyOf = (name: string): string => name.slice(0, name.indexOf('-'));

/**
 * Names a palette as Tailwind-style tokens, in input order. Each color takes the
 * name of its perceptually nearest Tailwind swatch. On a name collision it stays
 * within that nearest family and takes the closest unused step (e.g. a second
 * gray becomes `gray-300`, never an unrelated `indigo-300`); only if the whole
 * family is exhausted does it fall to the next-nearest family. Names are unique
 * and deterministic.
 */
export function nameColors(colors: string[]): string[] {
  const used = new Set<string>();
  return colors.map((hex) => {
    const c = toOklab(hex);
    if (c == null) {
      const name = numberedFallback(used);
      used.add(name);
      return name;
    }
    const ranked = REFERENCES.map((ref) => ({ name: ref.name, d: squaredDistance(c, ref) })).sort(
      (x, y) => x.d - y.d,
    );
    // Walk families nearest-first; within the first family that has a free step,
    // take its closest unused step (ranked is already distance-sorted).
    const triedFamilies = new Set<string>();
    let name: string | undefined;
    for (const candidate of ranked) {
      const family = familyOf(candidate.name);
      if (triedFamilies.has(family)) continue;
      triedFamilies.add(family);
      name = ranked.find((r) => familyOf(r.name) === family && !used.has(r.name))?.name;
      if (name != null) break;
    }
    name ??= numberedFallback(used);
    used.add(name);
    return name;
  });
}
